import { motion, useInView, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { useRef, useState } from "react";
import { Rocket, Globe, Megaphone, Palette, Filter, Mail, HandCoins, ChartNoAxesCombined } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const accentColors = ["#E92052", "#08214A", "#fda700", "#602d90"];
const accentBg = ["bg-brand-rosa", "bg-brand-azul", "bg-brand-amarillo", "bg-brand-uva"];

const serviceKeys = [
  { icon: Rocket, titleKey: "service.growth.title", descKey: "service.growth.desc" },
  { icon: Globe, titleKey: "service.ecosystem.title", descKey: "service.ecosystem.desc" },
  { icon: Megaphone, titleKey: "service.paid.title", descKey: "service.paid.desc" },
  { icon: Palette, titleKey: "service.creative.title", descKey: "service.creative.desc" },
  { icon: Filter, titleKey: "service.funnel.title", descKey: "service.funnel.desc" },
  { icon: Mail, titleKey: "service.automation.title", descKey: "service.automation.desc" },
  { icon: HandCoins, titleKey: "service.retention.title", descKey: "service.retention.desc" },
  { icon: ChartNoAxesCombined, titleKey: "service.data.title", descKey: "service.data.desc" },
];

const ServiceCard = ({
  service,
  i,
  inView,
}: {
  service: (typeof serviceKeys)[0];
  i: number;
  inView: boolean;
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  const { t } = useLanguage();
  const borderColor = accentColors[i % 4];
  const bgClass = accentBg[i % 4];

  // Tilt effect
  const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);
  const smoothRotateX = useSpring(rotateX, { stiffness: 200, damping: 20 });
  const smoothRotateY = useSpring(rotateY, { stiffness: 200, damping: 20 });

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    rotateY.set(x * 12);
    rotateX.set(-y * 12);
  };

  const handleMouseLeave = () => {
    setHovered(false);
    rotateX.set(0);
    rotateY.set(0);
  };

  return (
    <motion.div
      ref={cardRef}
      className="group relative p-6 md:p-7 rounded-2xl bg-white/[0.03] border border-white/[0.06] cursor-default h-full flex flex-col overflow-hidden"
      style={{
        rotateX: smoothRotateX,
        rotateY: smoothRotateY,
        transformPerspective: 800,
        transformStyle: "preserve-3d",
      }}
      initial={{ opacity: 0, y: 60, scale: 0.9, filter: "blur(8px)" }}
      animate={inView ? { opacity: 1, y: 0, scale: 1, filter: "blur(0px)" } : {}}
      transition={{
        duration: 0.7,
        delay: 0.15 + i * 0.1,
        ease: [0.22, 1, 0.36, 1],
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      whileHover={{
        y: -10,
        borderColor: `${borderColor}70`,
        backgroundColor: "rgba(255,255,255,0.07)",
        transition: { duration: 0.3 },
      }}
    >
      {/* Glow on hover */}
      <motion.div
        className="absolute inset-0 rounded-2xl pointer-events-none"
        animate={{
          boxShadow: hovered
            ? `inset 0 0 40px ${borderColor}15, 0 8px 32px ${borderColor}20`
            : "inset 0 0 0px transparent, 0 0px 0px transparent",
        }}
        transition={{ duration: 0.4 }}
      />

      {/* Accent line at top */}
      <motion.div
        className="absolute top-0 left-0 right-0 h-[2px] rounded-t-2xl"
        style={{ backgroundColor: borderColor }}
        initial={{ scaleX: 0 }}
        animate={hovered ? { scaleX: 1 } : { scaleX: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      />

      {/* Icon */}
      <motion.div
        className={`w-11 h-11 rounded-xl ${bgClass} flex items-center justify-center mb-5 relative z-10`}
        initial={{ scale: 0, rotate: -90 }}
        animate={inView ? { scale: 1, rotate: 0 } : {}}
        transition={{
          duration: 0.6,
          delay: 0.3 + i * 0.1,
          type: "spring",
          stiffness: 200,
          damping: 15,
        }}
        whileHover={{ scale: 1.15, rotate: 8 }}
      >
        <motion.div
          animate={hovered ? { rotate: [0, -10, 10, 0] } : {}}
          transition={{ duration: 0.5 }}
        >
          <service.icon className="w-5 h-5 text-white" />
        </motion.div>
      </motion.div>

      {/* Title */}
      <motion.h3
        className="text-sm font-black uppercase tracking-wide mb-3 text-white relative z-10"
        initial={{ opacity: 0, x: -25 }}
        animate={inView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.5, delay: 0.35 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
      >
        {t(service.titleKey)}
      </motion.h3>

      {/* Desc */}
      <motion.p
        className="text-sm text-white/55 leading-relaxed group-hover:text-white/75 transition-colors duration-300 flex-grow relative z-10"
        initial={{ opacity: 0, y: 10 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5, delay: 0.4 + i * 0.1 }}
      >
        {t(service.descKey)}
      </motion.p>

    </motion.div>
  );
};

const ServicesSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const gridY = useTransform(scrollYProgress, [0, 1], [80, -40]);
  const sectionScale = useTransform(scrollYProgress, [0, 0.15, 0.85, 1], [0.94, 1, 1, 0.96]);
  const sectionOpacity = useTransform(scrollYProgress, [0, 0.08, 0.92, 1], [0, 1, 1, 0.3]);
  const { t } = useLanguage();

  const titleWords = (t("services.title") || "").split(" ");

  return (
    <section id="servicios" className="pt-24 md:pt-32 pb-0 px-6 bg-foreground text-white overflow-hidden" ref={ref}>
      <motion.div className="max-w-7xl mx-auto" style={{ scale: sectionScale, opacity: sectionOpacity }}>
        <motion.div className="mb-16 text-left">
          <motion.p
            className="text-xs uppercase tracking-[0.25em] font-bold text-brand-rosa mb-4 text-primary-foreground"
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {t("services.tag")}
          </motion.p>

          {/* Word reveal title */}
          <h2 className="text-3xl md:text-6xl font-black tracking-tight lowercase">
            {titleWords.map((word, i) => {
              const isFuchsia = word.toLowerCase() === "servicios" || word.toLowerCase() === "services";
              return (
                <span key={i} className="inline-block overflow-hidden mr-[0.25em]">
                  <motion.span
                    className={`inline-block ${isFuchsia ? "text-brand-rosa" : "text-white"}`}
                    initial={{ y: "120%", rotateX: 40 }}
                    animate={inView ? { y: "0%", rotateX: 0 } : {}}
                    transition={{ duration: 0.7, delay: 0.15 + i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {word}
                  </motion.span>
                </span>
              );
            })}
          </h2>

          <motion.p
            className="text-base text-white/55 mt-4 max-w-lg"
            initial={{ opacity: 0, y: 20, filter: "blur(8px)" }}
            animate={inView ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
            transition={{ duration: 0.7, delay: 0.4 }}
          >
            {t("services.desc")}
          </motion.p>
        </motion.div>

        <motion.div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-fr" style={{ y: gridY }}>
          {serviceKeys.map((service, i) => (
            <ServiceCard key={service.titleKey} service={service} i={i} inView={inView} />
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default ServicesSection;
